import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"
import type {
    UsagePerTenantPerResourceReport,
    UsagePerTenantSummaryReport,
    UsageTenantRef,
} from "../../statisticsTypes"

import { colorForRank, otherTenantsColor } from "./tenantUsageColors"

// How many named tenants to show before collapsing the rest into "N other tenants".
const MAX_SUMMARY_TENANTS = 8
const MAX_RESOURCE_TENANTS = 3

function round(value: number): number {
    return Math.round(value * 10) / 10
}

// The tenant identifier is not stable across the API (companyId / userGroupId / id).
function tenantKey(tenant: UsageTenantRef): string {
    return String(tenant.companyId ?? tenant.id ?? tenant.userGroupId ?? tenant.name)
}

// ===== Summary (donut + legend) =====

export type TenantLegendItem = Readonly<{
    key: string
    name: string
    percentage: number
    numberOfBookings: number
    color: string
    isOther: boolean
    aggregatedCount: number
}>

export type TenantUsageSummaryViewModel = Readonly<{
    totalHoursBooked: number
    totalResources: number
    totalActiveTenants: number
    legend: ReadonlyArray<TenantLegendItem>
}>

export function toSummaryViewModel(report: UsagePerTenantSummaryReport): TenantUsageSummaryViewModel {
    const sorted = [...report.tenantUsages].sort((a, b) => b.percentageOfBookings - a.percentageOfBookings)
    const named = sorted.slice(0, MAX_SUMMARY_TENANTS)
    const rest = sorted.slice(MAX_SUMMARY_TENANTS)

    const legend: TenantLegendItem[] = named.map((tenant, index) => ({
        key: `tenant-${tenant.tenantName}-${index}`,
        name: tenant.tenantName,
        percentage: tenant.percentageOfBookings,
        numberOfBookings: tenant.numberOfBookings,
        color: colorForRank(index),
        isOther: false,
        aggregatedCount: 0,
    }))

    if (rest.length > 0) {
        legend.push({
            key: "tenant-other",
            name: "",
            percentage: round(rest.reduce((sum, tenant) => sum + tenant.percentageOfBookings, 0)),
            numberOfBookings: rest.reduce((sum, tenant) => sum + tenant.numberOfBookings, 0),
            color: otherTenantsColor,
            isOther: true,
            aggregatedCount: rest.length,
        })
    }

    return {
        totalHoursBooked: report.reportSummary.totalHoursBooked,
        totalResources: report.reportSummary.totalResources,
        totalActiveTenants: report.reportSummary.totalActiveTenants,
        legend,
    }
}

// ===== Per resource (segmented bars + expandable detail) =====

export type ResourceTenantSegment = Readonly<{
    key: string
    name: string
    usageRate: number
    color: string
    isOther: boolean
    aggregatedCount: number
}>

export type ResourceUsageViewModel = Readonly<{
    resourceId: number
    displayName: string
    resourceType: ResourceType | "UNKNOWN"
    segments: ReadonlyArray<ResourceTenantSegment>
}>

export type ResourceTypeGroupViewModel = Readonly<{
    resourceType: ResourceType | "UNKNOWN"
    resources: ReadonlyArray<ResourceUsageViewModel>
}>

type TenantUsage = Readonly<{ ref: UsageTenantRef; usageRate: number }>

function buildResourceSegments(tenants: ReadonlyArray<TenantUsage>): ReadonlyArray<ResourceTenantSegment> {
    const sorted = [...tenants].sort((a, b) => b.usageRate - a.usageRate)
    const named = sorted.slice(0, MAX_RESOURCE_TENANTS)
    const rest = sorted.slice(MAX_RESOURCE_TENANTS)

    const segments: ResourceTenantSegment[] = named.map((tenant, index) => ({
        key: tenantKey(tenant.ref),
        name: tenant.ref.name,
        usageRate: round(tenant.usageRate),
        color: colorForRank(index),
        isOther: false,
        aggregatedCount: 0,
    }))

    if (rest.length > 0) {
        segments.push({
            key: "other",
            name: "",
            usageRate: round(rest.reduce((sum, tenant) => sum + tenant.usageRate, 0)),
            color: otherTenantsColor,
            isOther: true,
            aggregatedCount: rest.length,
        })
    }

    return segments
}

const RESOURCE_TYPE_ORDER: ReadonlyArray<ResourceType | "UNKNOWN"> = [
    ResourceType.ROOM,
    ResourceType.MOBILE_AV,
    ResourceType.CAR,
    "UNKNOWN",
]

export function toResourceGroups(
    report: UsagePerTenantPerResourceReport,
    fallbackType: ResourceType | undefined,
): ReadonlyArray<ResourceTypeGroupViewModel> {
    type AggregatedResource = {
        displayName: string
        resourceType: ResourceType | "UNKNOWN"
        tenants: TenantUsage[]
    }

    const byResource = new Map<number, AggregatedResource>()

    for (const entry of report.usageRates) {
        const existing = byResource.get(entry.resource.id)
        if (existing) {
            existing.tenants.push({ ref: entry.tenant, usageRate: entry.usageRate })
        } else {
            byResource.set(entry.resource.id, {
                displayName: entry.resource.displayName,
                resourceType: entry.resource.resourceType ?? fallbackType ?? "UNKNOWN",
                tenants: [{ ref: entry.tenant, usageRate: entry.usageRate }],
            })
        }
    }

    const byType = new Map<ResourceType | "UNKNOWN", ResourceUsageViewModel[]>()

    byResource.forEach((resource, resourceId) => {
        const vm: ResourceUsageViewModel = {
            resourceId,
            displayName: resource.displayName,
            resourceType: resource.resourceType,
            segments: buildResourceSegments(resource.tenants),
        }

        const list = byType.get(resource.resourceType) ?? []
        list.push(vm)
        byType.set(resource.resourceType, list)
    })

    const groups: ResourceTypeGroupViewModel[] = []
    for (const type of RESOURCE_TYPE_ORDER) {
        const resources = byType.get(type)
        if (resources && resources.length > 0) {
            resources.sort((a, b) => a.displayName.localeCompare(b.displayName))
            groups.push({ resourceType: type, resources })
        }
    }

    return groups
}
